// Availability — when a trainer is available, and when they're away.
//
// Built around a pattern and a calendar rather than a list of rows. "Mon/Wed/Fri
// 3-6 until December" is one thought, and it used to take three passes through a
// form; now it's seven day pills and a range preset, and the calendar underneath
// fills in as you tap.
//
// The calendar is not a drawing of the schedule. It comes from
// POST /availability/preview, which runs the same computeSlots() the booking page
// runs — so what a trainer sees here is what a client sees there, because it is
// literally the same function answering a different question.
//
// Clicking a day turns it off, and if lessons are booked in it you get the
// riders' names before anything happens rather than a count in a confirm box.
// Turning a day off writes a schedule_exception — the same row a vacation
// writes — so "not this Thursday" and "away all next week" are one mechanism.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import AvailabilityCalendar, { OVERLAY_DOTS } from '../../components/schedule/AvailabilityCalendar';
import PatternBuilder from '../../components/schedule/PatternBuilder';
import {
  emptyPattern,
  slotsPerDay,
  patternFromGroup,
  nextTermFrom,
  splitIntoHours,
} from '../../lib/availabilityPattern';
import DayDetail from '../../components/schedule/DayDetail';
import DayHoursDialog from '../../components/schedule/DayHoursDialog';
import TimeOffRange from '../../components/schedule/TimeOffRange';
import ConvertSlot from '../../components/schedule/ConvertSlot';
import BlockSlotDialog from '../../components/schedule/BlockSlotDialog';
import { useAuth } from '../../lib/auth';
import { colorClass } from '../../lib/lessonTypes';
import { IconButton, PencilIcon, TrashIcon, CopyIcon } from '../../components/ui/icons';
import { fmtDate } from '../../lib/dates';

const SCHED = ['owner', 'manager', 'trainer'];

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

// "15:00" -> "3:00 PM"
function fmt(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function AvailabilityPage() {
  const { user, activeRole } = useAuth();
  const canSchedule = SCHED.includes(activeRole);
  const isManager = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();

  const [err, setErr] = useState('');
  const [trainerUserId, setTrainerUserId] = useState(user?.id ?? '');
  const [pattern, setPattern] = useState(() => emptyPattern(todayISO()));
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [editingLegacyId, setEditingLegacyId] = useState(null);
  const [impact, setImpact] = useState(null); // { from, to, lessons: [] }
  const [openDay, setOpenDay] = useState(null); // date expanded in the calendar
  const [editingHoursFor, setEditingHoursFor] = useState(null);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [converting, setConverting] = useState(null); // { slot, date }
  // The calendar leads and the builder is tucked under it: the calendar is
  // where the decisions get made, and the form is what you open to act on one.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [monthAnchor, setMonthAnchor] = useState(() => todayISO());
  const [overlayIds, setOverlayIds] = useState([]);
  const [busyDate, setBusyDate] = useState(null);

  const availQ = useQuery({
    queryKey: ['availability'],
    queryFn: () => api.get('/availability'),
    enabled: canSchedule,
    retry: false,
  });
  const typesQ = useQuery({
    queryKey: ['lesson-types'],
    queryFn: () => api.get('/lesson-types'),
    enabled: canSchedule,
  });
  const teamQ = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
    enabled: canSchedule && isManager,
    retry: false,
  });

  // One calendar month. The pattern's totals cover its whole range and come
  // back from the endpoint separately, so paging months doesn't change them.
  const from = monthStart(monthAnchor);
  const to = monthEnd(monthAnchor);

  // Whether the pattern is complete enough to preview. No weekdays isn't an
  // error, it's just nothing yet — so the calendar shows what's already saved.
  const previewable = pattern.weekdays.length > 0 && slotsPerDay(pattern) > 0;

  const previewQ = useQuery({
    queryKey: [
      'availability-preview',
      trainerUserId,
      from,
      to,
      overlayIds.join(','),
      editingPatternId,
      previewable ? JSON.stringify(pattern) : null,
    ],
    queryFn: () =>
      api.post('/availability/preview', {
        from,
        to,
        trainerUserId: trainerUserId || undefined,
        overlayTrainerIds: overlayIds,
        pattern: previewable ? pattern : undefined,
        excludePatternId: editingPatternId || undefined,
      }),
    enabled: canSchedule,
    // Keep the last calendar on screen while the next loads, so tapping a day
    // pill doesn't blink the whole grid away.
    placeholderData: (prev) => prev,
    retry: false,
  });

  // Time off ahead, for the strip under the calendar. Sixty days rather than
  // everything: the question it answers is "what have I got booked off", and
  // next April is not part of that question.
  const timeOffQ = useQuery({
    // Everyone's, filtered to whoever is on the calendar. Asking the endpoint
    // for one person meant the strip disagreed with the grid above it the
    // moment a second trainer was overlaid — their days showed as off up there
    // and were missing from the list of what's off.
    queryKey: ['time-off', from],
    queryFn: () =>
      api.get(`/time-off?from=${todayISO()}&to=${addDaysISO(todayISO(), 60)}`),
    enabled: canSchedule,
    retry: false,
  });

  // The builder mounts below the list it's adding to, which on a full month of
  // calendar is off the bottom of the screen. Opening something the user can't
  // see reads as the button having done nothing.
  // The group whose removal is being confirmed, plus the booked slots inside
  // it. Loaded before the dialog opens so it never renders an empty list that
  // then fills in — the whole point of it is the list.
  const [removing, setRemoving] = useState(null);
  // Time off queued for clearing, held until it's confirmed. A 16px × that
  // destroys a record on one tap, with any failure reported at the top of a
  // page you'd have to scroll back up to read, is not a thing you can tell
  // apart from a row that merely disappeared.
  const [clearing, setClearing] = useState(null);
  const [clearErr, setClearErr] = useState('');

  const builderRef = useRef(null);
  useEffect(() => {
    if (builderOpen) {
      builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [builderOpen]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['availability'] });
    qc.invalidateQueries({ queryKey: ['availability-preview'] });
    // Not covered by ['availability'] above: query keys match element by
    // element, so 'availability-day' is a different key, not a child of one.
    // Missing it left the open day panel reading a cached answer while the
    // calendar cell it dropped out of had already updated — the two
    // disagreeing about the same day, which is the one thing this panel sitting
    // inside the calendar exists to prevent.
    qc.invalidateQueries({ queryKey: ['availability-day'] });
    qc.invalidateQueries({ queryKey: ['time-off'] });
    qc.invalidateQueries({ queryKey: ['slots'] });
    qc.invalidateQueries({ queryKey: ['schedule'] });
  };

  // The slot being blocked, or null. Holds the whole slot rather than an id:
  // the dialog names the hour it is about, and re-deriving that from a list
  // that has since refetched is how a dialog ends up describing the wrong one.
  const [blockingSlot, setBlockingSlot] = useState(null);
  const [blockError, setBlockError] = useState(null);

  const blockSlot = useMutation({
    mutationFn: ({ slot, reason }) =>
      api.post('/slots/block', {
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        trainerUserId: slot.trainerUserId,
        reason,
      }),
    onSuccess: () => {
      setBlockingSlot(null);
      setBlockError(null);
      refresh();
    },
    onError: (e) => {
      const code = e?.body?.error;
      // The one refusal worth spelling out. "Conflict" would leave somebody
      // wondering; naming the rider says what to do about it.
      if (code === 'slot_booked') {
        const who = (e.body.riders || []).join(', ');
        setBlockError(
          `${who || 'Someone'} is booked in that slot. Move or cancel the ride first — blocking it ` +
            'would take the lesson off the calendar without telling anybody.',
        );
        return;
      }
      setBlockError(`That didn't block — ${code || 'no reason given'}${e?.status ? ` (${e.status})` : ''}.`);
    },
  });

  const unblockSlot = useMutation({
    // Scoped to the one day on purpose. A block can be part of a longer entry
    // ("Vet 2-4, Mon to Wed"), and putting back the Tuesday must not clear the
    // Monday and Wednesday with it. The API splits the range around the gap.
    mutationFn: ({ id, date }) => api.del(`/time-off/${id}?date=${date}`),
    onSuccess: () => {
      setBlockError(null);
      refresh();
    },
    onError: (e) =>
      setBlockError(
        `That didn't come back — ${e?.body?.error || e?.message}${e?.status ? ` (${e.status})` : ''}.`,
      ),
  });

  const savePattern = useMutation({
    mutationFn: async () => {
      const who = trainerUserId || undefined;

      // EDITING touches one hour and leaves its neighbours alone. That is the
      // whole reason the range is split in the first place — ten o'clock
      // becomes jumping without disturbing nine or eleven — so an edit must
      // never re-split and quietly replace three patterns with one.
      if (editingPatternId) {
        return api.patch(`/availability/pattern/${editingPatternId}`, {
          ...pattern,
          trainerUserId: who,
        });
      }

      // CREATING splits. One gesture, one pattern an hour, each independently
      // editable afterwards. Sequential rather than parallel: they are
      // separate patterns and a half-written set is easier to read than a
      // half-written one, but a burst of writes against D1 is not worth the
      // milliseconds.
      const blocks = splitIntoHours(pattern);
      let first = null;
      for (const b of blocks) {
        const created = await api.post('/availability/pattern', { ...b, trainerUserId: who });
        first = first ?? created;
      }
      // A block made one at a time has no pattern to edit, so saving replaces
      // it with a real pattern rather than leaving both behind.
      if (editingLegacyId) await api.del(`/availability/${editingLegacyId}`);
      return first;
    },
    onSuccess: () => {
      setErr('');
      setEditingPatternId(null);
      setEditingLegacyId(null);
      setPattern(emptyPattern(todayISO()));
      setBuilderOpen(false);
      refresh();
    },
    onError: (e) => setErr(friendly(e)),
  });

  // The first thing in the app that writes a slot override. One date, one
  // slot; the pattern behind it is not touched, which is the whole point and
  // is what the dialog says before it saves.
  const convertSlot = useMutation({
    mutationFn: ({ slot, date, body }) =>
      api.put('/slots/override', {
        trainerUserId: slot.trainerUserId ?? activeTrainer,
        date,
        startTime: slot.startTime,
        ...body,
      }),
    onSuccess: () => { setErr(''); setConverting(null); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const removeGroup = useMutation({
    mutationFn: (group) =>
      group.patternId
        ? api.del(`/availability/pattern/${group.patternId}`)
        : api.del(`/availability/${group.blocks[0].id}`),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const setDayOff = useMutation({
    mutationFn: ({ from, to, cancelIds, reason, startTime, endTime }) =>
      blockRange(from, to, cancelIds, trainerUserId, { reason, startTime, endTime }),
    // openDay is deliberately left alone: the panel stays put and re-renders
    // showing the day as off, which is the confirmation.
    onSuccess: () => {
      setErr(''); setImpact(null); setEditingHoursFor(null); setBusyDate(null); refresh();
    },
    onError: (e) => { setErr(friendly(e)); setBusyDate(null); },
  });

  const clearDayOff = useMutation({
    // With a date, one day comes out of the entry and the rest of the stretch
    // stays — splitting it in two if the day was in the middle. Without one,
    // the whole entry goes.
    mutationFn: ({ id, day }) =>
      api.del(`/time-off/${id}${day ? `?date=${day}` : ''}`),
    onSuccess: () => {
      setErr('');
      setEditingHoursFor(null);
      setBusyDate(null);
      setClearing(null);
      refresh();
    },
    // The dialog keeps its own copy so the message lands next to the button
    // that produced it. setErr still fires for the day-panel path, which has
    // no dialog of its own.
    onError: (e) => { setErr(friendly(e)); setBusyDate(null); setClearErr(friendly(e)); },
  });

  const setDayHours = useMutation({
    mutationFn: ({ date, ...hours }) =>
      api.put(`/availability/day/${date}`, { ...hours, trainerUserId: trainerUserId || undefined }),
    // The day stays open: you just changed it, so seeing the result is the
    // point. Only the dialog closes.
    onSuccess: () => { setErr(''); setEditingHoursFor(null); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const clearDayHours = useMutation({
    mutationFn: (date) =>
      api.del(`/availability/day/${date}?trainerUserId=${trainerUserId || ''}`),
    onSuccess: () => { setErr(''); setEditingHoursFor(null); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canSchedule) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">
          Availability is set by trainers and managers.
        </div>
      </div>
    );
  }

  const blocks = availQ.data?.availability ?? [];
  const types = typesQ.data?.lessonTypes ?? [];
  const trainers = isManager
    ? (teamQ.data?.members ?? []).filter((m) => ['owner', 'manager', 'trainer'].includes(m.role))
    : [{ userId: user?.id, name: user?.name, email: user?.email }];
  const activeTrainer = trainerUserId || user?.id || '';
  const groups = groupPatterns(blocks.filter((b) => b.trainerUserId === activeTrainer));
  // Everyone but whoever is focused — they're on the calendar by definition,
  // which is what keeps "Everyone" from ever meaning an empty calendar.
  const others = trainers.filter((t) => t.userId && t.userId !== activeTrainer);
  const allOn = others.length > 0 && others.every((t) => overlayIds.includes(t.userId));
  // Who the calendar is drawing. The strip lists time off for exactly these
  // people, so the two always describe the same set.
  const shownTrainerIds = [activeTrainer, ...overlayIds];
  const preview = previewQ.data;

  // Turning the open day off. The bookings are already listed in the panel, so
  // this only stops to ask when there are some — and then it asks with names.
  // Fired from the day dialog. Only stops to ask when something is booked —
  // and then it asks with names rather than a count.
  /**
   * Which slots in this pattern are booked.
   *
   * /impact answers "who is booked between two dates, optionally inside these
   * hours", which is one weekday short of the question: Tue/Thu 3–6 would
   * otherwise pick up Wednesday's 3–6 as well. The weekday filter is applied
   * here rather than in the endpoint because it's a display detail — nothing
   * is authorized or destroyed on the strength of it.
   */
  const askToRemove = async (g) => {
    const first = g.blocks[0];
    const weekdays = new Set(g.blocks.map((b) => b.weekday));
    const from = g.blocks.reduce(
      (min, b) => (b.effectiveFrom < min ? b.effectiveFrom : min),
      first.effectiveFrom,
    );
    // An open-ended pattern is asked about for a year. Past that, "and after
    // that too" is a sentence rather than a list.
    const untils = g.blocks.map((b) => b.effectiveUntil);
    const to = untils.some((u) => !u)
      ? addDaysISO(todayISO(), 365)
      : untils.reduce((max, u) => (u > max ? u : max), untils[0]);

    setErr('');
    try {
      const { lessons } = await api.get(
        `/availability/impact?from=${from > todayISO() ? from : todayISO()}&to=${to}` +
          `&trainerUserId=${activeTrainer}` +
          `&startTime=${first.startTime}&endTime=${first.endTime}`,
      );
      const booked = (lessons ?? []).filter((l) =>
        weekdays.has(new Date(l.date + 'T00:00:00Z').getUTCDay()),
      );
      setRemoving({ group: g, booked });
    } catch (e) {
      setErr(friendly(e));
    }
  };

  const markTimeOff = async ({ from, through, reason, startTime, endTime }) => {
    // `from` comes from the range dialog, which asks for the dates first.
    // Without it this is the day panel's route in, where the day has already
    // been picked and is the start by definition.
    const date = from || editingHoursFor || openDay;
    if (!date) return;
    const to = through && through > date ? through : date;
    setErr('');
    // Part of a day only disturbs the lessons inside that window, and asking
    // about the whole day would name riders whose lesson isn't going anywhere.
    const window =
      startTime && endTime ? `&startTime=${startTime}&endTime=${endTime}` : '';
    try {
      const { lessons } = await api.get(
        `/availability/impact?from=${date}&to=${to}&trainerUserId=${activeTrainer}${window}`,
      );
      const entry = { from: date, to, reason, startTime, endTime };
      if ((lessons ?? []).length === 0) {
        setBusyDate(date);
        setTimeOffOpen(false);
        setDayOff.mutate({ ...entry, cancelIds: [] });
        return;
      }
      // Whichever dialog asked steps aside for the confirmation, so the
      // riders being discussed are the thing on screen rather than being
      // hidden behind the form that disturbed them.
      setEditingHoursFor(null);
      setTimeOffOpen(false);
      setImpact({ ...entry, lessons });
    } catch (e) {
      setErr(friendly(e));
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 sm:pb-8 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Availability</h1>
      </header>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <section className="card p-4">
        {isManager && trainers.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {/* One rule: tapping a name shows that person, focused. Whoever was
                focused stays on as an overlay, so a tap never hides anybody —
                which is what lets a single control do both jobs. Removing is
                the one thing that needs its own target, so it gets the ×.
                The focused chip has no ×; you can't take off the person whose
                numbers the badges are counting, which also means the calendar
                can never end up empty. */}
            {trainers.map((t) => {
              const isPrimary = t.userId === activeTrainer;
              const i = overlayIds.indexOf(t.userId);
              const on = i !== -1;
              const label = t.name || t.email;

              if (isPrimary) {
                return (
                  <span
                    key={t.userId}
                    aria-current="true"
                    className={
                      'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ' +
                      'border bg-hunter text-white border-hunter'
                    }
                  >
                    <span className="w-2 h-2 rounded-full bg-white/80" />
                    {label}
                  </span>
                );
              }

              const focus = () => {
                const previous = activeTrainer;
                setTrainerUserId(t.userId);
                setOverlayIds((ids) => {
                  const kept = ids.filter((id) => id !== t.userId);
                  return previous && previous !== t.userId && !kept.includes(previous)
                    ? [...kept, previous]
                    : kept;
                });
              };

              if (!on) {
                return (
                  <button
                    key={t.userId}
                    type="button"
                    onClick={focus}
                    className={
                      'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ' +
                      'transition-colors bg-white text-ink-500 border-ink-200 hover:bg-ink-50'
                    }
                  >
                    {/* An empty ring where the dot will go, so the chip doesn't
                        change width as it comes on and the row doesn't reflow
                        under the cursor. */}
                    <span className="w-2 h-2 rounded-full border border-ink-300" />
                    {label}
                  </button>
                );
              }

              return (
                <span
                  key={t.userId}
                  className={
                    'inline-flex items-center gap-1.5 text-xs pl-3 pr-1.5 py-1 rounded-full ' +
                    'border bg-ink-800 text-white border-ink-800'
                  }
                >
                  <span
                    className={'w-2 h-2 rounded-full ' + OVERLAY_DOTS[i % OVERLAY_DOTS.length]}
                  />
                  <button type="button" onClick={focus} className="hover:underline">
                    {label}
                  </button>
                  <button
                    type="button"
                    aria-label={`Take ${label} off the calendar`}
                    onClick={() =>
                      setOverlayIds((ids) => ids.filter((id) => id !== t.userId))
                    }
                    className={
                      'ml-0.5 w-5 h-5 rounded-full hover:bg-white/25 inline-flex items-center ' +
                      'justify-center text-white/80 hover:text-white text-base font-semibold leading-none'
                    }
                  >
                    ×
                  </button>
                </span>
              );
            })}

            {/* Everybody at once. Four trainers means four taps to get the
                whole barn back after narrowing to one, and that's the round
                trip a manager makes constantly: look at everyone, focus
                somebody, look at everyone again.

                Last in the row and deliberately quiet: it's a control, not a
                person, so it carries no color dot and never sits where a name
                would. Below three trainers it's dead weight — with one other
                person their own chip already is this button — so it isn't
                rendered at all. */}
            {others.length > 1 && (
              <button
                type="button"
                aria-pressed={allOn}
                onClick={() => setOverlayIds(allOn ? [] : others.map((t) => t.userId))}
                className={
                  'inline-flex items-center text-xs px-2.5 py-1 rounded-full border ' +
                  'transition-colors ml-1 ' +
                  (allOn
                    ? 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                    : 'bg-white text-ink-500 border-dashed border-ink-300 hover:border-ink-500')
                }
              >
                {allOn ? `Just ${firstName(trainers.find((t) => t.userId === activeTrainer))}` : 'Everyone'}
              </button>
            )}
          </div>
        )}

        {previewQ.isLoading && !preview ? (
          <div className="h-72 bg-ink-50 rounded animate-pulse" />
        ) : (
          <AvailabilityCalendar
            from={preview?.from ?? from}
            to={preview?.to ?? to}
            today={preview?.today}
            days={preview?.days ?? []}
            daysOff={preview?.daysOff ?? []}
            trainers={preview?.trainers ?? []}
            primaryTrainerId={activeTrainer}
            busyDate={busyDate}
            disabled={setDayOff.isPending || clearDayOff.isPending}
            openDate={openDay}
            onOpenDay={(d) => setOpenDay((cur) => (cur === d ? null : d))}
            renderDayDetail={(d) => (
              <DayDetail
                date={d}
                trainers={preview?.trainers ?? []}
                primaryTrainerId={activeTrainer}
                dotFor={(id) => {
                  const i = (preview?.trainers ?? [])
                    .filter((t) => t.userId !== activeTrainer)
                    .findIndex((t) => t.userId === id);
                  return i === -1 ? 'bg-ink-300' : OVERLAY_DOTS[i % OVERLAY_DOTS.length];
                }}
                isOff={(preview?.daysOff ?? []).includes(d)}
                proposedSlots={
                  previewable && (preview?.days ?? []).find((x) => x.date === d)?.fromProposed
                    ? slotsPerDay(pattern) * pattern.capacity
                    : 0
                }
                busy={setDayOff.isPending || clearDayOff.isPending || blockSlot.isPending || unblockSlot.isPending}
                onClose={() => setOpenDay(null)}
                onEditHours={() => setEditingHoursFor(d)}
                // A manager may shape anyone's day; a trainer only their own.
                // The server enforces the same rule — this just doesn't offer
                // what would be refused.
                canBlock={isManager || activeTrainer === user?.id}
                onBlockSlot={(slot) => {
                  setBlockError(null);
                  setBlockingSlot(slot);
                }}
                onUnblock={(entry) => unblockSlot.mutate({ id: entry.id, date: d })}
                onConvert={(slot) => { setErr(''); setConverting({ slot, date: d }); }}
              />
            )}
            onPrevMonth={() => setMonthAnchor((m) => shiftMonth(m, -1))}
            onNextMonth={() => setMonthAnchor((m) => shiftMonth(m, 1))}
            onToday={() => setMonthAnchor(todayISO())}
          />
        )}

        <UpcomingTimeOff
          entries={(timeOffQ.data?.timeOff ?? []).filter(
            (e) => e.kind === 'staff' && shownTrainerIds.includes(e.userId),
          )}
          trainers={preview?.trainers ?? []}
          primaryTrainerId={activeTrainer}
          onOpen={(iso) => {
            // The month has to move with it, or tapping September 20 in
            // October's calendar opens a day that isn't on screen.
            setMonthAnchor(iso);
            setOpenDay(iso);
          }}
        />
      </section>

      <section className="card p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-medium">
            Manage Slots for{' '}
            {trainers.find((t) => t.userId === activeTrainer)?.name || 'this trainer'}
          </h2>
          {!builderOpen && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => { setErr(''); setTimeOffOpen(true); }}
              >
                Time off
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => {
                  setEditingPatternId(null);
                  setEditingLegacyId(null);
                  setPattern(emptyPattern(todayISO()));
                  setBuilderOpen(true);
                }}
              >
                Add Slots
              </button>
            </div>
          )}
        </div>

        {availQ.isLoading ? (
          <p className="text-sm text-ink-400">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-ink-400">
            Nothing set yet. Add hours and the calendar above will fill in.
          </p>
        ) : (
          <ul className="divide-y divide-ink-100">
            {groups.map((g) => (
              <PatternRow
                key={g.key}
                group={g}
                busy={removeGroup.isPending}
                onEdit={() => {
                  setEditingPatternId(g.patternId);
                  setEditingLegacyId(g.patternId ? null : g.blocks[0].id);
                  setPattern(patternFromGroup(g));
                  setBuilderOpen(true);
                  setErr('');
                }}
                onDuplicate={() => {
                  // Same shape, new stretch of time: the next term starts the
                  // day after this one ends, or today if it never ended.
                  setEditingPatternId(null);
                  setEditingLegacyId(null);
                  setPattern(nextTermFrom(g, todayISO()));
                  setBuilderOpen(true);
                  setErr('');
                }}
                onRemove={() => askToRemove(g)}
              />
            ))}
          </ul>
        )}
      </section>

      {builderOpen && (
        // scroll-mt clears the sticky header the shell puts over the top, so
        // "start" lands on the card's own heading rather than under the bar.
        <div ref={builderRef} className="scroll-mt-20">
        <PatternBuilder
          value={pattern}
          onChange={setPattern}
          types={types}
          trainers={trainers}
          isManager={isManager}
          trainerUserId={activeTrainer}
          onTrainerChange={setTrainerUserId}
          onSave={() => savePattern.mutate()}
          onCancel={() => {
            setEditingPatternId(null);
            setEditingLegacyId(null);
            setPattern(emptyPattern(todayISO()));
            setBuilderOpen(false);
          }}
          saving={savePattern.isPending}
          editing={!!(editingPatternId || editingLegacyId)}
          totalSlots={previewable ? preview?.totals?.slots ?? null : null}
        />
        </div>
      )}

      {blockingSlot && (
        <BlockSlotDialog
          slot={blockingSlot}
          trainerName={
            (preview?.trainers ?? []).find((t) => t.userId === blockingSlot.trainerUserId)?.name ?? null
          }
          busy={blockSlot.isPending}
          error={blockError}
          onConfirm={(reason) => blockSlot.mutate({ slot: blockingSlot, reason })}
          onClose={() => {
            setBlockingSlot(null);
            setBlockError(null);
          }}
        />
      )}

      {converting && (
        <ConvertSlot
          slot={converting.slot}
          date={converting.date}
          types={types}
          busy={convertSlot.isPending}
          error={err || null}
          onCancel={() => setConverting(null)}
          onSave={(body) => convertSlot.mutate({ ...converting, body })}
        />
      )}

      {timeOffOpen && (
        <TimeOffRange
          today={todayISO()}
          whose={
            activeTrainer === user?.id
              ? null
              : trainers.find((t) => t.userId === activeTrainer)?.name ?? null
          }
          busy={setDayOff.isPending || !!busyDate}
          error={err || null}
          onSubmit={markTimeOff}
          onClose={() => setTimeOffOpen(false)}
        />
      )}

      {editingHoursFor && (
        <DayHoursDialog
          date={editingHoursFor}
          trainerUserId={activeTrainer}
          saving={setDayHours.isPending || clearDayHours.isPending}
          onClose={() => setEditingHoursFor(null)}
          onSave={(hours) => setDayHours.mutate({ date: editingHoursFor, ...hours })}
          onClear={() => clearDayHours.mutate(editingHoursFor)}
          onMarkTimeOff={markTimeOff}
          onClearTimeOff={(entry, opts) => {
            // The day dialog steps aside for the confirmation, the way the
            // booking-impact one does. The day stays open behind both.
            setEditingHoursFor(null);
            setClearErr('');
            setClearing({ ...entry, day: opts?.day ?? null });
          }}
        />
      )}

      {clearing && (
        <ClearTimeOffDialog
          entry={clearing}
          who={
            (preview?.trainers ?? []).find((t) => t.userId === clearing.userId)?.name ?? null
          }
          saving={clearDayOff.isPending}
          error={clearErr}
          onConfirm={() => {
            setClearErr('');
            setBusyDate(clearing.day || clearing.startDate);
            clearDayOff.mutate({ id: clearing.id, day: clearing.day });
          }}
          onDismiss={() => { setClearing(null); setClearErr(''); }}
        />
      )}

      {removing && (
        <RemoveSlotsDialog
          label={describeGroup(removing.group)}
          booked={removing.booked}
          saving={removeGroup.isPending}
          onConfirm={() => {
            removeGroup.mutate(removing.group, { onSettled: () => setRemoving(null) });
          }}
          onDismiss={() => setRemoving(null)}
        />
      )}

      {impact && (
        <ImpactDialog
          from={impact.from}
          to={impact.to}
          lessons={impact.lessons}
          saving={setDayOff.isPending}
          onKeep={() => {
            setBusyDate(impact.from);
            setDayOff.mutate({ ...impact, lessons: undefined, cancelIds: [] });
          }}
          onCancelLessons={() => {
            setBusyDate(impact.from);
            setDayOff.mutate({
              ...impact,
              lessons: undefined,
              cancelIds: impact.lessons.map((l) => l.id),
            });
          }}
          onDismiss={() => setImpact(null)}
        />
      )}
    </div>
  );
}

/**
 * Time off in the next two months, in one line each.
 *
 * Paging through a calendar to find out what you've booked off is the wrong
 * shape for that question — it's a list, and it was a list on the old Time Off
 * card. What changed is that the card also owned CREATING time off, which meant
 * a day was managed in two places depending on whether you were adding hours or
 * taking them away. Making it read-only, next to the calendar it summarizes,
 * keeps the answer without splitting the verb.
 */
function UpcomingTimeOff({ entries, trainers, primaryTrainerId, onOpen }) {
  if (entries.length === 0) return null;

  // The same color that person carries on the calendar above. A name in the
  // list and a color in the grid that don't match is worse than no color.
  const overlay = trainers.filter((t) => t.userId !== primaryTrainerId);
  const toneFor = (userId) => {
    if (userId === primaryTrainerId) return 'bg-hunter text-white';
    const i = overlay.findIndex((t) => t.userId === userId);
    return i === -1
      ? 'bg-ink-400 text-white'
      : OVERLAY_DOTS[i % OVERLAY_DOTS.length] + ' text-white';
  };
  const nameFor = (userId) =>
    trainers.find((t) => t.userId === userId)?.name || 'Trainer';

  // Read-only, and that is the point. This list used to carry a × that deleted
  // a record on one tap — a fingertip-wide target, in a column of rows that all
  // look alike, for the one thing on the page you cannot undo. Time off is
  // cleared from the day it falls on, where you can see what's actually there
  // first. Every row here goes to that day.
  return (
    <div className="mt-4 rounded-lg border border-ink-200 bg-ink-50/70 p-3">
      <h3 className="text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">
        Upcoming time off
      </h3>
      <ul className="divide-y divide-ink-200/70">
        {entries.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => onOpen(e.startDate)}
              className={
                'w-full flex items-center gap-2.5 py-1.5 text-left rounded ' +
                'hover:bg-white focus:bg-white'
              }
            >
              <span
                className={
                  // A fixed width on the wider screens, so rows of
                  // different-length names still leave the dates in a column.
                  'shrink-0 px-2 py-1 rounded-full text-[11px] font-medium leading-none ' +
                  'max-w-[9rem] truncate sm:w-28 ' +
                  toneFor(e.userId)
                }
              >
                {nameFor(e.userId)}
              </span>
              <span className="text-sm shrink-0 sm:w-44">
                {e.startDate === e.endDate
                  ? fmtDayDate(e.startDate)
                  : `${fmtDayDate(e.startDate)} – ${fmtDayDate(e.endDate)}`}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-ink-500">
                {e.reason || 'Time off'}
                {!e.allDay && ` (${fmtClock(e.startTime)}–${fmtClock(e.endTime)})`}
              </span>
              <span className="text-ink-300 shrink-0 pr-1" aria-hidden="true">›</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Thu Sep 24.
 *
 * The weekday is the half somebody actually reasons with — "I'm away
 * Thursday" is how a barn talks about a day, and working out which day of the
 * week Sep 24 is takes a moment nobody should have to spend.
 */
function fmtDayDate(iso) {
  return fmtDate(iso, 'dayShort');
}

/** First name, for a control that sits in a row already showing full ones. */
function firstName(trainer) {
  return (trainer?.name || '').trim().split(/\s+/)[0] || 'you';
}

/** Sep 12. The year is implied by a list that only runs sixty days. */
function fmtShortDate(iso) {
  return fmtDate(iso, 'short');
}

function fmtClock(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  // 1 PM, not 1:00 PM: this is an aside in a line that already has a date.
  return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Turn a day off, optionally calling off the lessons in it first.
 *
 * Order matters: lessons are canceled BEFORE the day is blocked, so the change
 * alerts describe a real cancellation rather than racing a calendar that has
 * already stopped offering the time.
 */
async function blockRange(from, to, cancelIds, trainerUserId, opts = {}) {
  if (cancelIds.length > 0) await api.post('/lessons/cancel', { lessonIds: cancelIds });
  return api.post('/time-off', {
    userId: trainerUserId || undefined,
    startDate: from,
    endDate: to,
    // Times are all-or-nothing at the API, and null on both is what "all day"
    // means in the table — so an absent window has to stay absent, not become
    // an empty string the validator would read as half a range.
    startTime: opts.startTime || undefined,
    endTime: opts.endTime || undefined,
    reason: opts.reason || 'Time off',
  });
}

/**
 * The same pattern again, over the stretch of time after it.
 *
 * Barns run in terms and the next one usually looks like the last, so
 * duplicating keeps the days and hours and moves only the dates — starting the
 * day after the old one ended, for the same length it ran.
 */

/** First and last day of the month containing `iso`. */
function monthStart(iso) {
  return `${iso.slice(0, 7)}-01`;
}

function monthEnd(iso) {
  const d = new Date(iso.slice(0, 7) + '-01T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0); // day 0 of the next month is the last of this one
  return d.toISOString().slice(0, 10);
}

/**
 * Move `n` months, staying on the 1st.
 *
 * Anchored to the 1st on purpose: stepping from the 31st would land on the
 * 31st of a month that has none, and JavaScript would roll it into the month
 * after — so paging forward from January would skip February.
 */
function shiftMonth(iso, n) {
  const d = new Date(iso.slice(0, 7) + '-01T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Blocks to the patterns a person actually has in mind.
 *
 * Rows sharing a pattern_id are one thing. A row without one predates patterns
 * and is its own group of one, which is true rather than a special case.
 */
function groupPatterns(blocks) {
  const byPattern = new Map();
  const loose = [];
  for (const b of blocks) {
    if (!b.patternId) {
      loose.push(b);
      continue;
    }
    const g = byPattern.get(b.patternId) ?? {
      key: b.patternId,
      patternId: b.patternId,
      blocks: [],
    };
    g.blocks.push(b);
    byPattern.set(b.patternId, g);
  }
  const groups = [
    ...byPattern.values(),
    ...loose.map((b) => ({ key: b.id, patternId: null, blocks: [b] })),
  ];
  for (const g of groups) g.blocks.sort((a, b) => a.weekday - b.weekday);
  return groups.sort((a, b) => {
    const aw = Math.min(...a.blocks.map((x) => x.weekday));
    const bw = Math.min(...b.blocks.map((x) => x.weekday));
    return aw - bw || a.blocks[0].startTime.localeCompare(b.blocks[0].startTime);
  });
}


const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeGroup(g) {
  const days = g.blocks.map((b) => DAY_SHORT[b.weekday]).join('/');
  return `${days} ${fmt(g.blocks[0].startTime)}–${fmt(g.blocks[0].endTime)}`;
}

function PatternRow({ group, busy, onEdit, onDuplicate, onRemove }) {
  const b = group.blocks[0];
  const perWeek = group.blocks.length * b.slotCount * b.capacity;
  return (
    <li className="py-3 flex items-start gap-3">
      <span
        className={
          'w-1 self-stretch rounded-full shrink-0 ' + colorClass(b.lessonTypeColor, 'spine')
        }
      />
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{describeGroup(group)}</span>
        <span className="block text-sm text-ink-500">
          {[
            `${b.slotMinutes} min`,
            b.capacity > 1 ? `${b.capacity} riders` : 'private',
            b.lessonTypeName,
            `${perWeek} slots/wk`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="block text-xs text-ink-400 mt-0.5">
          {fmtDate(b.effectiveFrom, 'medium')}
          {b.effectiveUntil ? ` – ${fmtDate(b.effectiveUntil, 'medium')}` : ' onwards'}
        </span>
      </span>
      <span className="shrink-0 flex items-center gap-0.5">
        <IconButton icon={PencilIcon} label="Edit these hours" onClick={onEdit} />
        <IconButton
          icon={CopyIcon}
          label="Repeat these hours for the next stretch"
          onClick={onDuplicate}
        />
        <IconButton
          icon={TrashIcon}
          tone="danger"
          label="Remove these hours"
          disabled={busy}
          onClick={onRemove}
        />
      </span>
    </li>
  );
}

/**
 * Who is booked in the day being turned off.
 *
 * Names and times, not a count. A trainer who can see she is about to strand
 * Ada at three and Beth at half four makes a different decision from one shown
 * "2 lessons affected".
 */
/**
 * Clearing a stretch of time off.
 *
 * Nothing is lost that can't be typed again — but the dates and the reason DO
 * have to be typed again, and the control is a × the width of a fingertip in a
 * list of rows that all look alike. Worth one sentence and a confirm.
 *
 * It also gives a failed delete somewhere to appear. Before this the only
 * report was a line at the top of the page, and on a page this tall a clear
 * that quietly failed was indistinguishable from one that worked.
 */
function ClearTimeOffDialog({ entry, who, saving, error, onConfirm, onDismiss }) {
  const oneDay = entry.startDate === entry.endDate;
  const span = oneDay
    ? fmtDayDate(entry.startDate)
    : `${fmtDayDate(entry.startDate)} – ${fmtDayDate(entry.endDate)}`;
  // Trimming one day out of a stretch is a smaller thing than deleting the
  // stretch, and the dialog should not describe them the same way.
  const trimming = !!entry.day && !oneDay;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5">
        <h2 className="font-medium text-lg">
          {trimming ? 'Take this day out of the time off?' : 'Delete this time off?'}
        </h2>

        <p className="text-sm text-ink-500 mt-1">
          <strong className="text-ink-800">{entry.reason || 'Time off'}</strong>
          {who ? ` for ${who}` : ''}, {span}
          {!entry.allDay && ` (${fmtClock(entry.startTime)}–${fmtClock(entry.endTime)})`}.
        </p>

        <p className="text-sm text-ink-500 mt-2">
          {trimming ? (
            <>
              <strong className="text-ink-800">{fmtDayDate(entry.day)}</strong> goes back to the
              usual hours and can be booked again. The rest of the stretch stays.
            </>
          ) : (
            <>
              {oneDay ? 'That day goes' : 'Those days go'} back to the usual hours and can be
              booked again, and the entry is gone — putting it back means entering the dates and
              the reason again. Nothing already booked changes.
            </>
          )}
        </p>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <div className="flex flex-col gap-2 mt-4">
          <button
            type="button"
            className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
            disabled={saving}
            onClick={onConfirm}
          >
            {saving ? 'Working…' : trimming ? 'Take That Day Out' : 'Delete It'}
          </button>
          <button
            type="button"
            className="text-sm text-ink-500 hover:text-ink-800 py-1"
            disabled={saving}
            onClick={onDismiss}
          >
            Never mind
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Removing a set of recurring hours.
 *
 * This was a window.confirm, which is the one dialog in the app that can't
 * show a list — and a list is the entire question. "Lessons already booked
 * stay on the schedule" is a promise; the way to make a promise credible is to
 * name the lessons it's about.
 *
 * It talks about slots, because slots are what's being removed. A lesson and
 * the slot it sits in are different things, and this is exactly the moment the
 * difference matters: the slots go, the lessons don't.
 */
function RemoveSlotsDialog({ label, booked, saving, onConfirm, onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5">
        <h2 className="font-medium text-lg">Remove {label}?</h2>

        {booked.length === 0 ? (
          <p className="text-sm text-ink-500 mt-1">
            Nothing is booked in these slots. They&apos;ll stop being offered from now on.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-500 mt-1">
              {booked.length === 1 ? 'One of these slots is' : `${booked.length} of these slots are`}{' '}
              already booked. Those lessons <strong className="text-ink-700">stay on the
              schedule</strong> — only the empty slots stop being offered.
            </p>

            <ul className="my-3 divide-y divide-ink-100 max-h-56 overflow-y-auto">
              {booked.map((l) => (
                <li key={l.id} className="py-2 flex items-baseline gap-2 text-sm">
                  <span className="tabular-nums text-ink-500 shrink-0">
                    {fmtDayDate(l.date)} @ {l.startTime ? fmt(l.startTime) : 'TBD'}
                  </span>
                  <span className="text-ink-300 shrink-0" aria-hidden="true">·</span>
                  <span className="font-medium truncate">{l.riderName}</span>
                  {l.horseName && <span className="text-ink-400 truncate">· {l.horseName}</span>}
                </li>
              ))}
            </ul>
          </>
        )}

        <div className={'flex flex-col gap-2' + (booked.length === 0 ? ' mt-4' : '')}>
          <button
            type="button"
            className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
            disabled={saving}
            onClick={onConfirm}
          >
            {saving ? 'Working…' : 'Remove These Slots'}
          </button>
          <button
            type="button"
            className="text-sm text-ink-500 hover:text-ink-800 py-1"
            disabled={saving}
            onClick={onDismiss}
          >
            Never mind
          </button>
        </div>
      </div>
    </div>
  );
}

function ImpactDialog({ from, to, lessons, saving, onKeep, onCancelLessons, onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5">
        <h2 className="font-medium text-lg">
          Turning off {from === to ? fmtDate(from, 'medium') : `${fmtDate(from, 'medium')} – ${fmtDate(to, 'medium')}`}
        </h2>
        <p className="text-sm text-ink-500 mt-1">
          {lessons.length === 1 ? 'One lesson is' : `${lessons.length} lessons are`} booked in this
          time:
        </p>

        <ul className="my-3 divide-y divide-ink-100 max-h-56 overflow-y-auto">
          {lessons.map((l) => (
            <li key={l.id} className="py-2 flex items-baseline gap-2 text-sm">
              <span className="tabular-nums text-ink-500 shrink-0">
                {from === to ? '' : `${fmtShortDate(l.date)} @ `}
                {l.startTime ? fmt(l.startTime) : 'TBD'}
              </span>
              <span className="text-ink-300 shrink-0" aria-hidden="true">·</span>
              <span className="font-medium truncate">{l.riderName}</span>
              {l.horseName && <span className="text-ink-400 truncate">· {l.horseName}</span>}
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary" disabled={saving} onClick={onKeep}>
            {saving ? 'Working…' : 'Keep Them On The Books'}
          </button>
          <button
            type="button"
            className="btn-secondary text-rose-700"
            disabled={saving}
            onClick={onCancelLessons}
          >
            Cancel Them + Notify
          </button>
          <button
            type="button"
            className="text-sm text-ink-500 hover:text-ink-800 py-1"
            disabled={saving}
            onClick={onDismiss}
          >
            Never mind
          </button>
        </div>
      </div>
    </div>
  );
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'not_your_schedule':
      return 'You can only change your own hours.';
    case 'block_shorter_than_slot':
      return 'That window is shorter than one slot.';
    case 'end_before_start':
      return 'The end has to come after the start.';
    case 'invalid_time':
      return 'Check the times.';
    case 'invalid_date':
    case 'invalid_date_range':
      return 'Check the dates.';
    case 'partial_time_range':
      return 'Give both a start and an end time, or neither.';
    case 'lesson_type_not_found':
      return 'That lesson type no longer exists.';
    case 'trainer_not_in_barn':
    case 'not_in_barn':
      return 'That person isn’t in this barn.';
    case 'rider_not_found':
      return 'That rider no longer exists.';
    case 'rider_away':
      return 'That rider is marked away on this date.';
    // Everything the switch above doesn't name. It used to land here too, as
    // "Try again", which was advice rather than information and was wrong as
    // often as not — retrying a 404 forever will not fix a 404. The code and
    // the status are the only things that distinguish one unknown failure
    // from another, so they are what gets said.
    default: {
      const code = e?.body?.error || e?.message;
      const status = e?.status;
      if (!code && !status) return 'No response from the server. Check your connection.';
      return `Couldn’t save that${code ? `: ${code}` : ''}${status ? ` (${status})` : ''}.`;
    }
  }
}
